import React, { useState, useEffect } from 'react'
import { AlertCircle, CheckCircle, ChevronRight, ChevronDown } from 'lucide-react'
import { ReasoningPath as ReasoningPathType } from '@/service/api'
import { colors } from '@/styles/colors'
import { cn } from '@/lib/utils'
import { hasReasoningPathChanged } from '../reasoningPathUtils'
import { dedupReasoningSteps } from '@/features/chatV2/utils/dedupReasoningSteps'
import { useUIv2 } from '@/hooks/useUIv2'
import { useTranslation } from '@/i18n'
import ReasoningStepRow from './ReasoningStepRow'

interface ReasoningPathProps {
  reasoning_path: ReasoningPathType
}

const ReasoningPath: React.FC<ReasoningPathProps> = ({ reasoning_path }) => {
  const { steps: rawSteps, status } = reasoning_path
  const [isCollapsed, setIsCollapsed] = useState(status === 'completed' || status === 'error')
  const isV2 = useUIv2()
  const { t } = useTranslation()

  // Sync collapsed state with status changes:
  // - auto-expand when a new thinking phase starts
  // - auto-collapse when thinking completes
  useEffect(() => {
    setIsCollapsed(status === 'completed' || status === 'error')
  }, [status])

  // Defense in depth — render nothing if `steps` is missing or wrong-shaped
  // (e.g. a NestJS-written reasoning_path that's missing the steps array).
  // Hooks above run unconditionally to satisfy the Rules of Hooks.
  if (!Array.isArray(rawSteps)) {
    return null
  }

  // `ReasoningStep.completed` is optional, but `dedupReasoningSteps` requires a
  // concrete boolean (`DedupableStep`). Normalize undefined → false (the render
  // logic below already treats a missing `completed` as not-completed).
  const steps = dedupReasoningSteps(
    rawSteps.map((step) => ({ ...step, completed: step.completed ?? false }))
  )

  // V2 (Intui / Figma ReasoningTrigger) — logo + shimmer text + chevron
  if (isV2) {
    return (
      <div className="mb-3">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          <img
            src="/images/intuai-logo-icon.svg"
            alt=""
            className="w-4 h-4 flex-shrink-0"
            aria-hidden="true"
          />
          <span className={cn(
            'text-sm font-medium',
            status === 'in_progress'
              ? 'thinking-shimmer'
              : 'text-orange-600 dark:text-orange-400'
          )}>
            {t('chat:messages.thinking', { defaultValue: 'Thinking' })}
          </span>
          {isCollapsed ? (
            <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
          ) : (
            <ChevronDown size={16} className="text-gray-400 dark:text-gray-500" />
          )}
        </button>

        {/* No steps yet (the stream's first frames often carry an empty
            list): render no container — an expanded empty box reads as a
            broken blank rectangle under "Thinking". */}
        {!isCollapsed && steps.length > 0 && (
          <div className="mt-2 ml-6 space-y-2 p-3 bg-gray-50 dark:bg-dark-muted rounded-lg border border-gray-200 dark:border-dark-border">
            {steps.map((step) => (
              <ReasoningStepRow key={step.step} step={step} status={status} />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Legacy (pre-V2) — bordered card with status pill, exact markup from main.
  const getStatusColor = (s: string) => {
    switch (s) {
      case 'completed':
        return colors.success[600]
      case 'in_progress':
        return colors.info[600]
      case 'stopped':
        return colors.warning[600]
      case 'error':
        return colors.gray[600]
      default:
        return colors.gray[600]
    }
  }

  const getStatusText = (s: string) => {
    switch (s) {
      case 'completed':
        return t('chat:messages.status.completed', { defaultValue: 'Completed' })
      case 'in_progress':
        return t('chat:messages.status.in_progress', { defaultValue: 'In Progress' })
      case 'stopped':
        return t('chat:messages.status.stopped', { defaultValue: 'Stopped' })
      case 'error':
        return t('chat:messages.status.error', { defaultValue: 'Unable to find an answer' })
      default:
        return t('chat:messages.status.unknown', { defaultValue: 'Unknown' })
    }
  }

  return (
    <div className="rounded-lg mb-3 border bg-gray-50 dark:bg-dark-muted border-gray-200 dark:border-dark-border">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between p-3 text-left rounded-lg"
        style={{ cursor: 'pointer' }}
      >
        <h4 className="text-sm font-medium flex items-center gap-2 text-gray-700 dark:text-gray-300">
          {isCollapsed ? (
            <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
          ) : (
            <ChevronDown size={16} className="text-gray-400 dark:text-gray-500" />
          )}
          {status === 'in_progress' && (
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: colors.info[500] }} />
          )}
          {status === 'completed' ? (
            <CheckCircle size={16} style={{ color: colors.success[500] }} />
          ) : status === 'error' ? (
            <AlertCircle size={16} style={{ color: colors.gray[600] }} />
          ) : null}
          {t('chat:messages.thinking', { defaultValue: 'Thinking' })}
        </h4>
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-dark-text-muted">
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-opacity-10" style={{ color: getStatusColor(status) }}>
            {getStatusText(status)}
          </span>
        </div>
      </button>

      {!isCollapsed && steps.length > 0 && (
        <div className="space-y-2 px-3 pb-3">
          {steps.map((step) => (
            <ReasoningStepRow key={step.step} step={step} status={status} />
          ))}
        </div>
      )}
    </div>
  )
}

// Reuse the same logic as hasReasoningPathChanged so the two layers
// (state-level reference preservation + component-level memo) stay in sync.
export default React.memo(ReasoningPath, (prev, next) =>
  !hasReasoningPathChanged(prev.reasoning_path, next.reasoning_path)
)
